import React, { useState, useEffect } from 'react';
import VMasker from 'vanilla-masker';
import { toast } from 'react-toastify';

import { Grid, Typography, Tooltip, IconButton, CircularProgress, Box } from '@mui/material';
import { PageWrapper, Input, Switch, Button, Modal } from '../../components';

import { Add, Check, Delete } from '@mui/icons-material';

import {
  useDispatch,
  useSelector,
  viewReceiverList,
  addReceiver,
  editReceiver,
  deleteReceiver
} from '../../stores';

import { ReceiverLockedErrorType } from '../../services/Receivers/types';

import { validations } from './validations';
import { theme } from '../../theme';

import { handleErrors } from '../../utils/Helpers';

import useResponsiveMode from '../../utils/useResponsiveMode';

export const Receivers: React.FC = () => {
  const dispatch = useDispatch();

  const responsiveMode = useResponsiveMode();

  const { receivers, isReceiversLoading } = useSelector(state => state.receiversReducer);

  const [formData, setFormData] = useState<{
    id?: string;
    alias: string;
    accountId: string;
    percentAmount: string;
    devicesOwner: number;
    remainingCommission: number;
    errors?: { [key: string]: string }
  }[]>([
    {
      id: '',
      alias: '',
      accountId: '',
      percentAmount: '',
      devicesOwner: 0,
      remainingCommission: 0,
      errors: {}
    }
  ]);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const [lockedModalOpen, setLockedModalOpen] = useState(false);
  const [lockedModalMessage, setLockedModalMessage] = useState('');

  useEffect(() => {
    dispatch(viewReceiverList());
  }, []);

  useEffect(() => {
    if (receivers.length == 0) {
      setFormData([
        {
          alias: '',
          accountId: '',
          percentAmount: '',
          devicesOwner: 0,
          remainingCommission: 0,
          errors: {}
        }
      ]);
    }

    if (receivers.length > 0) {
      setFormData([
        {
          alias: '',
          accountId: '',
          percentAmount: '',
          devicesOwner: 0,
          remainingCommission: 0,
          errors: {}
        },
        ...receivers.map(receiver => {
          return {
            ...receiver,
            percentAmount: receiver.percentAmount?.replaceAll('.', ',')
          }
        })
      ]);
    }
  }, [receivers]);

  const handleRemove = (id: string | undefined) => {
    if (id) {
      return dispatch(deleteReceiver({ id }))
        .then(res => {
          const payload = res.payload as ReceiverLockedErrorType;

          if (payload?.status == 423) {
            handleLockedAction(payload);
            return;
          }

          if (res.meta.requestStatus == 'fulfilled') {
            toast.success('Recebedor removido com sucesso');
            dispatch(viewReceiverList());
            return;
          }

          toast.error('Houve um erro ao remover o recebedor.');
        });
    }
  }

  const handleEdit = (data: {
    id?: string;
    alias: string;
    accountId: string;
    percentAmount: string;
    devicesOwner: number;
    remainingCommission: number;
  }) => {
    const { id, percentAmount, devicesOwner, remainingCommission } = data;

    if (id) {
      return dispatch(editReceiver({ id, percentAmount: +percentAmount.replaceAll(',', '.'), devicesOwner, remainingCommission }))
        .then(res => {
          const payload = res.payload as ReceiverLockedErrorType;

          if (payload?.status == 423) {
            handleLockedAction(payload);
            return;
          }

          if (res.meta.requestStatus == 'fulfilled') {
            toast.success('Recebedor editado com sucesso.');
            dispatch(viewReceiverList());
            return;
          }

          toast.error('Houve um erro ao editar o recebedor.');
        });
    }
  }

  const handleSubmit = () => {
    validations.validate(formData[0], { abortEarly: false })
      .then(() => {
        const { alias, accountId, percentAmount, devicesOwner, remainingCommission } = formData[0];

        return dispatch(addReceiver({ alias, accountId, percentAmount: +percentAmount.replaceAll(',', '.'), devicesOwner, remainingCommission }))
          .then(res => {
            const payload = res.payload as ReceiverLockedErrorType;

            if (payload?.status == 423) {
              handleLockedAction(payload);
              return;
            }

            if (res.meta.requestStatus == 'fulfilled') {
              toast.success('Recebedor cadastrado com sucesso.');
              dispatch(viewReceiverList());
              return;
            }

            toast.error('Houve um erro ao cadastrar o recebedor.');
          });
      })
      .catch((err) => {
        const updatedFormData = formData.map((data, index) => {
          if (index == 0) {
            return {
              ...data,
              errors: handleErrors(err)
            }
          }

          return data;
        });

        setFormData(updatedFormData);
      });
  }

  const handleOpenConfirmModal = (action: () => void) => {
    setPendingAction(() => action);
    setConfirmModalOpen(true);
  };

  const handleCloseConfirmModal = () => {
    setConfirmModalOpen(false);
    setPendingAction(null);
  };

  const handleConfirmAction = () => {
    pendingAction?.();
    handleCloseConfirmModal();
  };

  const handleCloseLockedModal = () => {
    setLockedModalOpen(false);
    setLockedModalMessage('');
  };

  const handleLockedAction = (response: ReceiverLockedErrorType): void => {
    setLockedModalMessage(response.msg);
    setLockedModalOpen(true);
  };

  return (
    <PageWrapper>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant={responsiveMode != 'phone' ? 'h4' : 'h5'}
            margin={1}
            display='flex'
            justifyContent='flex-start'
            alignItems='center'
            color={theme.palette.text.main}
          >
            Adicionar Recebedores do Split
          </Typography>
        </Grid>
      </Grid>
      {formData.map((_, index) => {
        return (
          <Grid container spacing={2} style={{ marginTop: 10 }}>
            <Grid item xs={12}>
              <Grid container justifyContent='space-between'>
                <Typography>
                  Recebedor
                </Typography>
                {(index != 0) && (
                  <Grid item>
                    <Tooltip placement='top' title='Remover recebedor'>
                      <IconButton onClick={() => handleOpenConfirmModal(() => handleRemove(formData[index].id))}>
                        {isReceiversLoading ? (
                          <CircularProgress size='1rem' />
                        ) : (
                          <Delete color='error' />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip placement='top' title='Atualizar recebedor'>
                      <IconButton onClick={() => handleOpenConfirmModal(() => handleEdit(formData[index]))}>
                        {isReceiversLoading ? (
                          <CircularProgress size='1rem' />
                        ) : (
                          <Check color='secondary' />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Input
                data-testid='receiver'
                type='text'
                margin='normal'
                label='Cliente / Cidade'
                value={formData[index].alias}
                disabled={index != 0}
                error={formData[index]?.errors?.alias}
                helperText={formData[index]?.errors?.alias}
                onChange={value => {
                  const updatedFormData = formData.map((data, formDataIndex) => {
                    return index == formDataIndex
                      ? { ...formData[formDataIndex], alias: value }
                      : data
                  });

                  setFormData(updatedFormData);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                data-testid='accountId'
                type='text'
                margin='normal'
                label='ID da conta'
                value={formData[index].accountId}
                disabled={index != 0}
                error={formData[index]?.errors?.accountId}
                helperText={formData[index]?.errors?.accountId}
                onChange={value => {
                  const updatedFormData = formData.map((data, formDataIndex) => {
                    return index == formDataIndex
                      ? { ...formData[formDataIndex], accountId: value }
                      : data
                  });

                  setFormData(updatedFormData);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                data-testid='percentAmount'
                type='text'
                margin='normal'
                label='Percentual'
                value={formData[index].percentAmount || ''}
                onChange={(value) => {
                  const formattedDecimal = VMasker
                    .toMoney(value, { precision: 2, separator: '.', delimiter: '-' })
                    .replaceAll('-', '');

                  if (+formattedDecimal > 100) {
                    return;
                  }

                  const updatedFormData = formData.map((data, formDataIndex) => {
                    return index == formDataIndex
                      ? { ...formData[formDataIndex], percentAmount: formattedDecimal.replaceAll('.', ',') }
                      : data
                  });

                  setFormData(updatedFormData);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Switch
                label='Detentor das máquinas POS'
                checked={!!formData[index].devicesOwner}
                onChange={(value) => {
                  const updatedFormData = formData.map((data, formDataIndex) => {
                    return index == formDataIndex
                      ? { ...formData[formDataIndex], devicesOwner: value.target.checked ? 1 : 0 }
                      : data
                  });

                  setFormData(updatedFormData);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Switch
                label='Destinar sobra do Split para este recebedor (exemplo: Venda do fiscal)'
                checked={!!formData[index].remainingCommission}
                onChange={(value) => {
                  const updatedFormData = formData.map((data, formDataIndex) => {
                    return index == formDataIndex
                      ? { ...formData[formDataIndex], remainingCommission: value.target.checked ? 1 : 0 }
                      : data
                  });

                  setFormData(updatedFormData);
                }}
              />
            </Grid>
            {(index == 0) && (
              <Grid item xs={12} md={3}>
                <Button
                  sx={{ width: '100%' }}
                  color='secondary'
                  variant='contained'
                  label='Adicionar Recebedor'
                  startIcon={<Add />}
                  loading={isReceiversLoading}
                  onClick={() => handleOpenConfirmModal(handleSubmit)}
                />
              </Grid>
            )}
          </Grid>
        );
      })}
      <Modal
        open={confirmModalOpen}
        onClose={handleCloseConfirmModal}
        title='Têm certeza que deseja realizar essa ação?'
      >
        <Box>
          <Typography>
            Essa ação interfere em todas as regras de Split cadastradas no sistema atualmente. O processo de atualização das regras após essa ação pode demorar em torno de 10 minutos para persistir em todas as regras de Split.
          </Typography>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              mt: '20px',
            }}
          >
            <Button
              variant='contained'
              color='secondary'
              label='Confirmar'
              style={{ width: '100%', margin: 0 }}
              onClick={handleConfirmAction}
            />
            <Button
              variant='outlined'
              color='error'
              label='Cancelar'
              style={{ width: '100%', margin: 0, marginTop: 10 }}
              onClick={handleCloseConfirmModal}
            />
          </Box>
        </Box>
      </Modal>
      <Modal
        open={lockedModalOpen}
        onClose={handleCloseLockedModal}
        title='Erro'
      >
        <Box>
          <Typography>{lockedModalMessage}</Typography>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              mt: '20px',
            }}
          >
            <Button
              variant='contained'
              color='secondary'
              label='Continuar'
              style={{ width: '100%', margin: 0 }}
              onClick={handleCloseLockedModal}
            />
          </Box>
        </Box>
      </Modal>
    </PageWrapper>
  );
}